import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { AssetTimeseriesProperty } from '@archimed/models';
import { ButtonComponent } from '@totalenergiescode/tds-angular';
import {
  ASSET_REPOSITORY,
  ASSETS_API,
  TIMESERIES_REPOSITORY,
} from '../../../application/ports';
import { ExplorerStore, ExplorerStoreType } from '../../../application/state';
import {
  LoadAssetChildrenUseCase,
  LoadAssetTypesUseCase,
  LoadAssetsUseCase,
} from '../../../application/use-cases';
import { Asset, TypeNode, ViewMode } from '../../../domain/models';
import {
  AssetDefinitionsApiAdapter,
  AssetInstancesApiAdapter,
  TimeseriesApiAdapter,
} from '../../../infrastructure/adapters';
import { assetToTreeNode, typeNodeToTreeNode } from '../../mappers';
import { AssetDetailsComponent } from '../asset-details/asset-details.component';
import { ExplorerToolbarComponent } from '../explorer-toolbar/explorer-toolbar.component';
import { LoadingSpinnerComponent } from '../loading-spinner/loading-spinner.component';
import { TreeNode } from '../tree-view/tree-node.model';
import { TreeViewComponent } from '../tree-view/tree-view.component';
import { TypeDetailsComponent } from '../type-details/type-details.component';
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TreeViewComponent,
    ButtonComponent,
    ExplorerToolbarComponent,
    AssetDetailsComponent,
    TypeDetailsComponent,
    LoadingSpinnerComponent,
  ],
  providers: [
    LoadAssetsUseCase,
    LoadAssetTypesUseCase,
    LoadAssetChildrenUseCase,
    AssetInstancesApiAdapter,
    { provide: ASSET_REPOSITORY, useExisting: AssetInstancesApiAdapter },
    { provide: ASSETS_API, useClass: AssetDefinitionsApiAdapter },
    { provide: TIMESERIES_REPOSITORY, useClass: TimeseriesApiAdapter },
  ],
  selector: 'lib-asset-explorer',
  standalone: true,
  styleUrl: './asset-explorer.component.scss',
  templateUrl: './asset-explorer.component.html',
})
export class AssetExplorerComponent implements OnInit {
  private readonly store: ExplorerStoreType = inject(ExplorerStore);
  private readonly loadAssetsUseCase = inject(LoadAssetsUseCase);
  private readonly loadAssetTypesUseCase = inject(LoadAssetTypesUseCase);
  private readonly loadAssetChildrenUseCase = inject(LoadAssetChildrenUseCase);

  readonly ViewMode = ViewMode;

  isLoading = this.store.isLoading;
  error = this.store.error;
  nodeError = this.store.nodeError;
  selectedAsset = this.store.selectedAsset;
  viewMode = this.store.viewMode;

  private readonly _selectedTypeNode = signal<TypeNode | null>(null);
  readonly selectedTypeNode = this._selectedTypeNode.asReadonly();

  readonly selectedTypeAssets = computed(() => {
    const typeNode = this._selectedTypeNode();
    if (!typeNode) return [];
    return this.store.hierarchy().findByType(typeNode.type.toString());
  });

  readonly selectedAssetTimeseriesProperties = computed(
    (): AssetTimeseriesProperty[] => {
      const asset = this.selectedAsset();
      if (!asset) return [];
      const entityType = asset.entityType;
      if (!entityType) return [];
      const typeDef = this.store
        .assetTypes()
        .find((t) => t.assetType === entityType);
      return typeDef?.timeseriesProperties ?? [];
    },
  );

  readonly dataSource = computed((): TreeNode[] => {
    const mode = this.viewMode();

    if (mode === ViewMode.TYPE) {
      return this.store.typeHierarchy().map((t) => typeNodeToTreeNode(t, true));
    }

    return Array.from(this.store.hierarchy().getRoots()).map((a: Asset) =>
      assetToTreeNode(a, true),
    );
  });

  ngOnInit(): void {
    void this.loadAssetsUseCase.execute();
    void this.loadAssetTypesUseCase.execute();
  }

  onTreeNodeSelected(node: TreeNode): void {
    if (this.viewMode() === ViewMode.TYPE) {
      const typeNode = this._findTypeNodeById(node.value);
      if (typeNode) this.onTypeNodeSelect(typeNode);
    } else {
      const asset = this.store.hierarchy().findById(node.value);
      if (asset) {
        const currentId = this.store.selectedAssetId();
        this.store.selectAsset(currentId === asset.id ? null : asset.id);
      }
    }
  }

  onCloseDetails(): void {
    this.store.selectAsset(null);
    this._selectedTypeNode.set(null);
  }

  onNavigateToAsset(assetId: string): void {
    this.store.selectAsset(assetId);
  }

  onPropertyUpdated(event: { propertyName: string; value: unknown }): void {
    const selectedId = this.store.selectedAssetId();
    if (selectedId) {
      this.store.updateAssetProperty(
        selectedId,
        event.propertyName,
        event.value,
      );
    }
  }

  onTypeNodeSelect(typeNode: TypeNode): void {
    const current = this._selectedTypeNode();
    if (current?.getId() === typeNode.getId()) {
      this._selectedTypeNode.set(null);
      return;
    }
    this._selectedTypeNode.set(typeNode);
  }

  onNodeExpanded(node: TreeNode): void {
    if (this.viewMode() === ViewMode.INSTANCE) {
      this.store.setNodeError(null);
      void this.loadAssetChildrenUseCase.execute(node.value);
    }
  }

  dismissNodeError(): void {
    this.store.setNodeError(null);
  }

  onViewModeChange(mode: ViewMode): void {
    this.store.setViewMode(mode);
    this._selectedTypeNode.set(null);

    if (mode === ViewMode.TYPE) {
      void this.loadAssetTypesUseCase.execute();
    } else {
      void this.loadAssetsUseCase.execute();
    }
  }

  onRefresh(): void {
    this.store.clear();
    if (this.viewMode() === ViewMode.TYPE) {
      void this.loadAssetTypesUseCase.execute();
    } else {
      void this.loadAssetsUseCase.execute();
    }
  }

  private _findTypeNodeById(id: string): TypeNode | null {
    const search = (nodes: ReadonlyArray<TypeNode>): TypeNode | null => {
      for (const node of nodes) {
        if (node.getId() === id) return node;
        const found = search(node.children);
        if (found) return found;
      }
      return null;
    };
    return search(this.store.typeHierarchy());
  }
}
